<script lang="ts">
import { defineComponent, PropType, computed } from 'vue'
import { defaultProps, defaultFunction } from '@/components/_parials/buttonCore'

export default defineComponent({
  name: 'ButtonFacebook',

  props: {
    ...defaultProps,

    /**
     * クリックした際に発行する emit の名称
     * @description ルート要素の class（o-action__sns）に合わせた名前付きの click を既定とする
     */
    emitName: {
      type: String,
      default: 'click:sns',
    },

    /**
     * ボタンを各サービスカラーで塗りつぶすか
     * @description false の場合は白
     */
    filled: {
      type: Boolean,
      default: true,
    },

    /**
     * 対象ソーシャルサービス名
     */
    service: {
      type: String as PropType<'facebook' | 'twitter' | 'google' | 'apple'>,
      required: true,
    },
  },

  setup(props, { emit }) {
    const {
      // data
      wrapper,

      // computed
      buttonTagName,
      hasSlot,

      // methods
      runEvent,
    } = defaultFunction(props, emit)

    return {
      // data
      wrapper,

      // computed
      buttonTagName,
      hasSlot,

      // methods
      runEvent,
    }
  }
})
</script>

<template>
  <div
    ref="wrapper"
    :class="[
      'o-action__sns',
      { 'is-fill': filled },
      { 'is-small': isFontSmall },
      { 'is-bold': isFontBold },
      { 'is-shadow': hasShadow },
      { 'is-narrowMargin': narrowMargin },
    ]"
    :data-name="service">
    <a
      v-if="isLink"
      class="o-action__sns--item"
      :href="href"
      :target="target">
      <span class="o-action__sns--icon">
        <i
          :class="[
            `i-sns__${service}`,
            { 'is-white': filled },
          ]" />
      </span>

      <span
        v-if="hasSlot"
        class="o-action__sns--label">
        <slot />
      </span>
    </a>
    <button
      v-else
      class="o-action__sns--item"
      type="button"
      :name="name"
      :disabled="disabled"
      @click="runEvent">
      <span class="o-action__sns--icon">
        <i
          :class="[
            `i-sns__${service}`,
            { 'is-white': filled },
          ]" />
      </span>

      <span
        v-if="hasSlot"
        class="o-action__sns--label">
        <slot />
      </span>
    </button>
  </div>
</template>

<style lang="sass" scoped>
.o-action__sns
  $root: &
  $margin-size-px: 24
  $margin-narrow-size-px: 16
  margin:
    top: changeSizePx2Em($margin-size-px)
    left: auto
    right: auto

  &:first-child
    margin-top: 0

  & &--item
    $button-height-px: 48
    width: 100%
    min-height: #{$button-height-px}px
    display: flex
    justify-content: center
    align-items: center
    line-height: 1
    color: $font-black
    text:
      align: center
      decoration: none
    padding: 0.5em 1em
    background-color: rgba($bg-white, 1)
    border: 1px solid $border-x-light-gray
    border-radius: 100vh
    +transition($duration: $transition-middle)

    &:not(:disabled)
      +mouseover
        background-color: rgba($bg-xx-light-cyan, 1)
        box-shadow: $button-shadow

    &:disabled
      background-color: rgba($bg-gray, 1)
      border: 1px solid #ccc

  & &--label
    margin-left: 0.5em

    &:first-child
      margin-left: 0

  &.is-fill
    #{$root}--item
      position: relative
      overflow: hidden
      color: $font-white
      border: none

      &::before
        content: ""
        width: 0
        height: 0
        display: block
        position: absolute
        top: 50%
        left: 50%
        transform: translate(-50%, -50%)
        background-color: rgba($bg-white, 0.2)
        border-radius: 50%
        opacity: 0
        +transition($duration: $transition-long)

      &:not(:disabled)
        +mouseover
          &::before
            width: 200%
            height: 200%
            opacity: 1

      &:disabled
        background-color: rgba($bg-gray, 1)

    &[data-name="facebook"]
      #{$root}--item
        background-color: rgba($bg-facebook, 1)

    &[data-name="twitter"]
      #{$root}--item
        background-color: rgba($bg-twitter, 1)

    &[data-name="google"]
      #{$root}--item
        background-color: rgba($bg-google, 1)

    &[data-name="apple"]
      #{$root}--item
        background-color: rgba($bg-apple, 1)

  &.is-small
    $button-height-px: 40
    #{$root}--item
      min-height: changeSizePx2Em($button-height-px, $font-small-px)
      font-size: $font-small
      padding: 0.5em 1.8em

  &.is-bold
    #{$root}--item
      font-weight: bold

  &.is-shadow
    #{$root}--item
      &:not(:disabled)
        +mouseover
          box-shadow: $button-shadow

  &.is-narrowMargin
    margin-top: changeSizePx2Em($margin-narrow-size-px)

    &:first-child
      margin-top: 0

  +breakpoint
    $margin-size-px: 16
    margin-top: changeSizePx2Em($margin-size-px)

    &:first-child
      margin-top: 0

.i-sns
  &__facebook,
  &__twitter,
  &__google,
  &__apple
    $size-px: 21
    width: changeSizePx2Em($size-px)
    height: changeSizePx2Em($size-px)
    display: block
    background:
      size: contain
      repeat: no-repeat

  &__facebook
    background-image: url(@/images/icon/sns/icon-facebook_01.svg)

    &.is-white
      background-image: url(@/images/icon/sns/icon-facebook_02.svg)

  &__twitter
    background-image: url(@/images/icon/sns/icon-twitter_01.svg)

  &__google
    background-image: url(@/images/icon/sns/icon-google_01.svg)

  &__apple
    background-image: url(@/images/icon/sns/icon-apple_01.svg)
</style>
